/**
 * Avisos e Campanhas — constantes do Master.
 *
 * Os inteiros aqui sao os MESMOS que a API grava na coluna. Copias que precisam
 * andar no mesmo commit:
 *  - MaMSistemaPonto.Domain/Enums/EBroadcast*.cs (legado, EF6)
 *  - MarQ.Backoffice.V2/src/enums/broadcast.ts
 *  - este arquivo (Master)
 *  - MarQ.Backoffice/assets/js/angular/constantes.js (BROADCAST_TYPE)
 *  - MarQ.Web2/src/vue/views/components/BroadcastBanner.vue (BROADCAST_TYPE)
 *
 * O Master nao tem i18n (grep '$t(' em src/vue nao retorna nada), entao os
 * rotulos sao pt-BR inline, como no resto do projeto.
 */

export const BROADCAST_TYPE = {
  NONE: 0,
  RELEASE_NOTE: 1,
  PROMOTION_BANNER: 2,
};

/**
 * TODOS os tipos que a coluna pode conter, inclusive os que nao se pode mais
 * escolher. E daqui que a listagem tira o rotulo: uma campanha gravada com um
 * tipo aposentado tem de continuar aparecendo com nome, e nao como
 * "Desconhecido".
 *
 * Hoje nenhum tipo esta aposentado, entao a lista e igual a de baixo. Os dois
 * nomes continuam separados porque querem dizer coisas diferentes, e quem
 * aposentar o proximo tipo so tira dali.
 */
export const BROADCAST_TYPE_OPTIONS_ALL = [
  {
    value: BROADCAST_TYPE.RELEASE_NOTE,
    label: 'Nota de versão (modal)',
    hint: 'Novidades do produto. Abre em modal e não leva a lugar nenhum.',
  },
  {
    value: BROADCAST_TYPE.PROMOTION_BANNER,
    label: 'Banner promocional',
    hint: 'Faixa no topo da dashboard. O card inteiro leva ao destino.',
  },
];

/** O que o autor pode escolher hoje. */
export const BROADCAST_TYPE_OPTIONS = BROADCAST_TYPE_OPTIONS_ALL;

/** Bitmask. Backoffice V2 e Backoffice legado sao a MESMA superficie. */
/** Imagem do banner: limite de upload no Master. */
export const BROADCAST_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export const BROADCAST_SURFACE = {
  BACKOFFICE: 1,
  WEB: 2,
};

/**
 * Opcoes de "onde aparece" no passo 1. Backoffice e UM bit de proposito: o
 * mesmo usuario e devolvido entre V2 e legado por rota em tempo de execucao
 * (checkLocationAndRedirectToV2), entao um bit so para V2 seria cara ou coroa
 * sobre qual tela ele abriu. Decisao 12 do design.
 */
export const BROADCAST_SURFACE_OPTIONS = [
  {
    value: BROADCAST_SURFACE.BACKOFFICE,
    label: 'Backoffice',
    hint: 'V2 e legado (qual dos dois a empresa vê é configuração dela).',
  },
  {
    value: BROADCAST_SURFACE.WEB,
    label: 'Web',
    hint: 'App do colaborador.',
  },
];

/** Lista de bits marcados -> bitmask que a API grava. */
export const surfacesToMask = (list) =>
  (list || []).reduce((mask, bit) => mask | bit, 0);

/** Bitmask gravada -> lista de bits marcados. Usada na edicao. */
export const maskToSurfaces = (mask) =>
  BROADCAST_SURFACE_OPTIONS.filter((opt) => (mask & opt.value) !== 0).map(
    (opt) => opt.value
  );

/**
 * Bitmask. Em qual balde cada usuario cai e decidido no SERVIDOR
 * (BroadcastAdminGate.AudienceOf); aqui e so o que o autor escolhe.
 *
 * MANAGERS e 4 porque 3 seria ADMINS|EMPLOYEES. "Gestor" e o colaborador
 * (Role 1) dono de pelo menos uma equipe — NAO o EUserRole.Manager = 3, que e
 * administrador secundario e conta como administrador.
 */
export const BROADCAST_AUDIENCE = {
  ADMINS: 1,
  EMPLOYEES: 2,
  MANAGERS: 4,
};

/** Ordem de exibicao: do publico mais restrito ao mais amplo. */
export const BROADCAST_AUDIENCE_OPTIONS = [
  { value: BROADCAST_AUDIENCE.ADMINS, label: 'Administradores' },
  { value: BROADCAST_AUDIENCE.MANAGERS, label: 'Gestores de equipe' },
  { value: BROADCAST_AUDIENCE.EMPLOYEES, label: 'Colaboradores' },
];

/** O que o antigo "Somente administradores e gestores" marcado significava. */
export const BROADCAST_AUDIENCE_DEFAULT = [
  BROADCAST_AUDIENCE.ADMINS,
  BROADCAST_AUDIENCE.MANAGERS,
];

export const audienceToMask = (bits) => bits.reduce((mask, bit) => mask | bit, 0);

/** Bitmask gravada -> bits marcados, na ordem das opcoes. Usada na edicao. */
export const maskToAudience = (mask) =>
  BROADCAST_AUDIENCE_OPTIONS.filter((opt) => (mask & opt.value) !== 0).map(
    (opt) => opt.value
  );

/**
 * Cor da faixa do banner. A faixa carrega APENAS cor — sem icone e sem rotulo
 * (verificado no node 31806:197048 do Figma).
 */
export const BROADCAST_COLOR = {
  PRIMARY: 1,
  SUCCESS: 2,
  WARNING: 3,
  ERROR: 4,
  PURPLE: 5,
  CYAN: 6,
  ORANGE: 7,
  YELLOW: 8,
  BRICK: 9,
};

/** Hex apenas para o seletor de cor e o preview DO MASTER. */
export const BROADCAST_COLOR_SWATCHES = [
  { value: BROADCAST_COLOR.PRIMARY, hex: '#1B59F8', label: 'Azul' },
  { value: BROADCAST_COLOR.SUCCESS, hex: '#12B937', label: 'Verde' },
  { value: BROADCAST_COLOR.WARNING, hex: '#FFB800', label: 'Âmbar' },
  { value: BROADCAST_COLOR.ERROR, hex: '#FF445D', label: 'Vermelho' },
  { value: BROADCAST_COLOR.PURPLE, hex: '#9747FF', label: 'Roxo' },
  { value: BROADCAST_COLOR.CYAN, hex: '#00B5DD', label: 'Ciano' },
  { value: BROADCAST_COLOR.ORANGE, hex: '#FF9900', label: 'Laranja' },
  { value: BROADCAST_COLOR.YELLOW, hex: '#FFC700', label: 'Amarelo' },
  { value: BROADCAST_COLOR.BRICK, hex: '#F65834', label: 'Terracota' },
];

/** Limite de produto sobre uma coluna NVARCHAR(MAX). */
export const BROADCAST_BODY_MAX = 1000;
export const BROADCAST_TITLE_MAX = 200;
export const BROADCAST_SUBTITLE_MAX = 300;
export const BROADCAST_CTA_LABEL_MAX = 80;
export const BROADCAST_URL_MAX = 2000;

export const isBroadcastModalType = (type) =>
  type === BROADCAST_TYPE.RELEASE_NOTE;

export const isBroadcastBannerType = (type) =>
  type === BROADCAST_TYPE.PROMOTION_BANNER;

/**
 * Rotulo do botao da modal: o que o autor escreveu, ou "Entendi". O botao
 * so reconhece a modal — modal nao tem destino.
 */
export const modalButtonLabel = (ctaLabel) => String(ctaLabel || '').trim() || 'Entendi';

/**
 * Um destino so vale se for caminho interno comecando com '/' (mas nao '//',
 * que e protocol-relative e sai do dominio) ou http(s) absoluto. Mesma
 * allow-list do V2 (utils/helpers/broadcastNavigate.ts) — o Master valida antes
 * de deixar salvar para o erro aparecer aqui, nao no cliente.
 */
export const isValidBroadcastUrl = (url) => {
  const value = (url || '').trim();
  if (!value) return false;
  if (value.indexOf('//') === 0) return false;
  if (value.charAt(0) === '/') return true;
  return /^https?:\/\//i.test(value);
};



// WEBPACK FOOTER //
// ./src/vue/pages/restricted/broadcast/broadcastEnums.js